import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  Image,
  Users,
  Calculator,
  ClipboardList,
  LogOut,
  ExternalLink,
  ImageIcon,
  Briefcase,
  PenSquare,
  Settings,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Toaster } from '../../components/ui/sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hub-medico-apex/artifacts/ft6xenx5_Simbolo%20Apex.png';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/posts', icon: FileText, label: 'Artigos' },
  { to: '/admin/categories', icon: FolderOpen, label: 'Categorias' },
  { to: '/admin/services', icon: Briefcase, label: 'Serviços' },
  { to: '/admin/site-content', icon: PenSquare, label: 'Conteúdo do Site' },
  { to: '/admin/site-images', icon: ImageIcon, label: 'Imagens do Site' },
  { to: '/admin/media', icon: Image, label: 'Mídia' },
  { to: '/admin/leads', icon: Users, label: 'Leads' },
  { to: '/admin/calculator-leads', icon: Calculator, label: 'Calculadora' },
  { to: '/admin/pesquisa-plantao', icon: ClipboardList, label: 'Pesquisa Plantão', testId: 'pesquisa-plantao' },
  { to: '/admin/settings', icon: Settings, label: 'Configurações' },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-slate-900 text-white flex flex-col z-50">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Apex" className="w-10 h-10" />
            <div>
              <h1 className="font-heading font-bold text-lg">Apex Admin</h1>
              <p className="text-xs text-slate-400">Painel de Controle</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`
                    }
                    data-testid={`admin-nav-${item.testId || item.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            <span>Ver site</span>
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-slate-800 transition-colors w-full"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-40">
          <div>
            <p className="text-sm text-slate-500">Bem-vindo,</p>
            <p className="font-medium text-slate-900">{user?.name || 'Administrador'}</p>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default AdminLayout;
